import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import straivLogo from '@/assets/straiv-logo-share.png';
import { ShareableWorkout } from '../ShareWorkoutCard';
import { useUnitFormatters } from '@/hooks/useUnits';
import { kgToDisplayWeight } from '@/lib/units';

interface ShareTemplateProps {
  workout: ShareableWorkout;
}

const formatShareTitle = (title: string) => {
  if (!title?.trim()) return title;
  const s = title
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim();
  return s.split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
};

const formatTime = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = Math.floor(minutes % 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

/**
 * Transparent gym share template - full details.
 * Lists each exercise with sets x reps @ weight, designed to be composed over a user photo.
 */
export const ShareTemplateGymExercises = forwardRef<HTMLDivElement, ShareTemplateProps>(
  ({ workout }, ref) => {
    const { t } = useTranslation();
    const { formatWeight, units, weightLabel } = useUnitFormatters();
    const exercises = workout.exercises || [];
    // Show up to 8 exercises; collapse the rest into a "+N more" line.
    const visible = exercises.slice(0, 8);
    const overflow = exercises.length - visible.length;
    const volumeDisplay = workout.total_volume
      ? Math.round(kgToDisplayWeight(workout.total_volume, units))
      : 0;

    return (
      <div
        ref={ref}
        className="relative overflow-hidden flex flex-col items-center"
        style={{
          width: '1080px',
          height: '1980px',
          background: 'transparent',
          padding: 0,
        }}
      >
        {/* Header block - logo + title */}
        <div
          className="flex flex-col items-center"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '160px',
            padding: '0 80px',
          }}
        >
          {/* The logo asset is a 1000×1000 PNG where the visible mark only
              occupies the middle ~270 vertical pixels (rows ~365..635). Using
              object-fit shrinks the visible mark; instead we render the image
              at its natural aspect (380×380) inside a wrapper that crops away
              the transparent top/bottom padding. The visible logo ends up at
              roughly its original on-screen size without claiming the full
              square in the layout flow. */}
          <div
            style={{
              width: '380px',
              height: '110px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <img
              src={straivLogo}
              alt="STRAIV"
              style={{ width: '380px', height: 'auto', display: 'block' }}
            />
          </div>
          <div
            className="font-bold text-center"
            style={{
              fontSize: '64px',
              color: '#0AFFC2',
              lineHeight: 1.1,
              marginTop: '8px',
              marginBottom: '20px',
              textShadow: '0 4px 24px rgba(0,0,0,0.55)',
            }}
          >
            {formatShareTitle(workout.title)}
          </div>
          <div
            className="uppercase tracking-widest text-center"
            style={{
              fontSize: '28px',
              color: 'rgba(255,255,255,0.75)',
              letterSpacing: '4px',
              lineHeight: 1.4,
              paddingBottom: '4px',
              textShadow: '0 2px 12px rgba(0,0,0,0.55)',
            }}
          >
            {`${workout.total_sets || 0} sets · ${volumeDisplay} ${weightLabel} · ${formatTime(workout.duration_minutes)}`}
          </div>
        </div>

        {/* Exercises card - frosted/glass to stay legible on any photo */}
        <div
          style={{
            position: 'absolute',
            left: '80px',
            right: '80px',
            top: '620px',
            background: 'rgba(15,15,18,0.55)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(10,255,194,0.25)',
            borderRadius: '40px',
            padding: '56px 56px 40px',
            boxShadow: '0 24px 64px rgba(0,0,0,0.45)',
          }}
        >
          {/* Column headers */}
          <div
            className="flex items-center"
            style={{
              gap: '24px',
              paddingBottom: '20px',
              marginBottom: '12px',
              borderBottom: '1px solid rgba(255,255,255,0.12)',
            }}
          >
            <span
              className="uppercase tracking-widest"
              style={{ flex: 1, fontSize: '24px', color: 'rgba(255,255,255,0.55)', letterSpacing: '3px' }}
            >
              {t('components.shareTemplatesExtra.exerciseHeader')}
            </span>
            <span
              className="uppercase tracking-widest text-center"
              style={{ width: '110px', fontSize: '24px', color: 'rgba(255,255,255,0.55)', letterSpacing: '3px' }}
            >
              {t('components.shareTemplates.sets')}
            </span>
            <span
              className="uppercase tracking-widest text-center"
              style={{ width: '110px', fontSize: '24px', color: 'rgba(255,255,255,0.55)', letterSpacing: '3px' }}
            >
              {t('components.shareTemplatesExtra.repsHeader')}
            </span>
            <span
              className="uppercase tracking-widest text-right"
              style={{ width: '160px', fontSize: '24px', color: 'rgba(255,255,255,0.55)', letterSpacing: '3px' }}
            >
              {t('components.shareTemplatesExtra.weightHeader')}
            </span>
          </div>

          {/* Exercise rows */}
          {visible.length === 0 ? (
            <div
              className="text-center"
              style={{ padding: '60px 0', fontSize: '32px', color: 'rgba(255,255,255,0.55)' }}
            >
              {t('components.shareTemplatesExtra.noExercisesRecorded')}
            </div>
          ) : (
            <div className="flex flex-col">
              {visible.map((ex, i) => (
                <div
                  key={`${ex.name}-${i}`}
                  className="flex items-center"
                  style={{
                    gap: '24px',
                    padding: '22px 0',
                    borderBottom: i < visible.length - 1 ? '1px solid rgba(255,255,255,0.07)' : 'none',
                  }}
                >
                  <span
                    className="font-semibold"
                    style={{
                      flex: 1,
                      fontSize: '34px',
                      color: '#FFFFFF',
                      // Generous line-height + small padding-bottom prevents descenders
                      // (the tail of "p", "y", "g") from being clipped by overflow:hidden
                      lineHeight: 1.5,
                      paddingBottom: '4px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatShareTitle(ex.name)}
                  </span>
                  <span
                    className="font-bold text-center"
                    style={{ width: '110px', fontSize: '36px', color: '#0AFFC2' }}
                  >
                    {ex.sets || 0}
                  </span>
                  <span
                    className="font-bold text-center"
                    style={{ width: '110px', fontSize: '36px', color: '#0AFFC2' }}
                  >
                    {ex.reps ?? '—'}
                  </span>
                  <span
                    className="font-bold text-right"
                    style={{ width: '160px', fontSize: '32px', color: '#FFFFFF' }}
                  >
                    {ex.weight_kg ? formatWeight(ex.weight_kg) : '—'}
                  </span>
                </div>
              ))}
              {overflow > 0 && (
                <div
                  className="text-center"
                  style={{
                    paddingTop: '24px',
                    fontSize: '26px',
                    color: 'rgba(255,255,255,0.5)',
                  }}
                >
                  {overflow === 1 ? t('components.shareTemplatesExtra.moreExercise', { count: overflow }) : t('components.shareTemplatesExtra.moreExercises', { count: overflow })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <p
          className="tracking-wide text-center"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: '120px',
            fontSize: '28px',
            color: 'rgba(255,255,255,0.55)',
            textShadow: '0 2px 8px rgba(0,0,0,0.5)',
          }}
        >
          {t('components.shareTemplatesExtra.poweredBy')}
        </p>
      </div>
    );
  }
);

ShareTemplateGymExercises.displayName = 'ShareTemplateGymExercises';
